import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Send, AlertCircle, Download } from 'lucide-react';
import { useSettings } from '../lib/usePublicData';
import { pushDataLayerEvent } from './Analytics';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FORMULAS = [
    { value: 'Algemene partnerbrochure', label: 'Algemene partnerbrochure' },
    { value: 'ZOMERKICK', label: 'ZOMERKICK' },
    { value: 'ZOMERBOOST', label: 'ZOMERBOOST' },
    { value: 'ZOMERVIBES', label: 'ZOMERVIBES' },
    { value: 'ZOMERGOESTING', label: 'ZOMERGOESTING' },
    { value: 'Nog niet zeker', label: 'Nog niet zeker' },
];

// Map slug uit `#brochure?formule=<slug>` → select-value.
const FORMULA_SLUG_MAP = {
    zomerkick: 'ZOMERKICK',
    zomerboost: 'ZOMERBOOST',
    zomervibes: 'ZOMERVIBES',
    zomergoesting: 'ZOMERGOESTING',
};

const initialState = {
    name: '',
    company: '',
    email: '',
    phone: '',
    formula: 'Algemene partnerbrochure',
    message: '',
    hp_field: '',
};

const validate = (s) => {
    const errs = {};
    if (!s.name.trim()) errs.name = 'Vul je naam in.';
    if (!s.company.trim()) errs.company = 'Vul je bedrijfsnaam in.';
    if (!s.email.trim()) errs.email = 'Vul je e-mail in.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email)) errs.email = 'Geen geldig e-mailadres.';
    if (!s.formula) errs.formula = 'Kies een formule.';
    return errs;
};

export const BrochureForm = () => {
    const { data: settings } = useSettings();
    const { hash, key } = useLocation();
    const [state, setState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle'); // idle | submitting | success | error
    const [errMsg, setErrMsg] = useState('');

    // Pre-select formule wanneer URL hash een ?formule=<slug> bevat
    // (bv. /#brochure?formule=zomerkick). Werkt zowel bij navigatie via een
    // chip-Link binnen de site als bij directe deep-link uit een andere tab.
    // `key` triggert opnieuw na same-anchor klikken zodat een tweede klik op
    // dezelfde chip de selectie ook weer juist zet.
    useEffect(() => {
        const q = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
        if (!q) return;
        const params = new URLSearchParams(q);
        const slug = (params.get('formule') || '').toLowerCase();
        const mapped = FORMULA_SLUG_MAP[slug];
        if (mapped) {
            setState((s) => ({ ...s, formula: mapped }));
            // Maak URL clean (behoud #brochure, drop de query) zodat sharebare
            // URL's geen pre-fill achterlaten. replaceState voorkomt history-pollutie.
            if (typeof window !== 'undefined' && window.history?.replaceState) {
                window.history.replaceState(null, '', `${window.location.pathname}#brochure`);
            }
        }
    }, [hash, key]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setState((s) => ({ ...s, [name]: value }));
        if (errors[name]) setErrors((es) => ({ ...es, [name]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(state);
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setStatus('submitting');
        setErrMsg('');
        try {
            await axios.post(`${API_BASE}/brochure-requests`, state, {
                headers: { 'Content-Type': 'application/json' },
            });
            setStatus('success');
            // Fire conversion event for GTM/GA4/Pixel (no-op when consent declined or scripts not loaded)
            pushDataLayerEvent('brochure_request_submit', { formula: state.formula });
            setState(initialState);
        } catch (err) {
            setStatus('error');
            const msg =
                err.response?.data?.detail ||
                'Er ging iets mis bij het versturen. Probeer het later opnieuw.';
            setErrMsg(typeof msg === 'string' ? msg : 'Er ging iets mis.');
        }
    };

    if (status === 'success') {
        return (
            <div
                id="brochure"
                data-testid="brochure-success"
                className="bg-[var(--mint-soft)] border-2 border-[var(--green)] rounded-[24px] p-8 sm:p-10 text-center shadow-[0_6px_0_var(--green-dark)]"
            >
                <CheckCircle2 className="mx-auto text-[var(--green)] mb-4" size={48} />
                <h3 className="font-display zz-outline-title-dark text-2xl sm:text-3xl">
                    Bedankt voor je interesse!
                </h3>
                <p className="mt-4 text-[var(--green-dark)] max-w-xl mx-auto">
                    We bezorgen je zo snel mogelijk de partnerbrochure en bekijken graag samen de mogelijkheden.
                </p>
                <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    data-testid="brochure-success-reset"
                    className="zz-btn zz-btn--primary mt-7 !text-xs"
                >
                    Nieuw verzoek indienen
                </button>
            </div>
        );
    }

    return (
        <form
            id="brochure"
            onSubmit={onSubmit}
            noValidate
            data-testid="brochure-form"
            className="bg-cream border-2 border-[var(--green)] rounded-[24px] p-6 sm:p-10 shadow-[0_6px_0_var(--green-dark)]"
        >
            <h3
                className="font-display zz-outline-title-dark text-center"
                style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
            >
                PARTNERBROCHURE AANVRAGEN
            </h3>
            <p className="mt-3 text-sm sm:text-base text-[#006b44]/80 text-center max-w-xl mx-auto">
                Laat je gegevens achter en we bezorgen je de volledige partnerbrochure.
            </p>

            {/* Honeypot — display:none */}
            <div style={{ display: 'none' }} aria-hidden="true">
                <label>
                    Niet invullen
                    <input
                        type="text"
                        name="hp_field"
                        tabIndex={-1}
                        autoComplete="off"
                        value={state.hp_field}
                        onChange={onChange}
                    />
                </label>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field
                    label="Naam *"
                    name="name"
                    value={state.name}
                    error={errors.name}
                    onChange={onChange}
                    autoComplete="name"
                    testId="brochure-input-name"
                />
                <Field
                    label="Bedrijf *"
                    name="company"
                    value={state.company}
                    error={errors.company}
                    onChange={onChange}
                    autoComplete="organization"
                    testId="brochure-input-company"
                />
                <Field
                    label="E-mail *"
                    name="email"
                    type="email"
                    value={state.email}
                    error={errors.email}
                    onChange={onChange}
                    autoComplete="email"
                    testId="brochure-input-email"
                />
                <Field
                    label="Telefoon"
                    name="phone"
                    type="tel"
                    value={state.phone}
                    error={errors.phone}
                    onChange={onChange}
                    autoComplete="tel"
                    testId="brochure-input-phone"
                />

                <div className="sm:col-span-2">
                    <label className="block text-xs font-display uppercase tracking-wider text-[var(--green-dark)] mb-2">
                        Interesse formule
                    </label>
                    <select
                        name="formula"
                        value={state.formula}
                        onChange={onChange}
                        data-testid="brochure-input-formula"
                        className="w-full bg-cream border-2 border-[var(--green)] rounded-full px-4 py-3 text-base text-[var(--green-dark)] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--green)]"
                    >
                        {FORMULAS.map((f) => (
                            <option key={f.value} value={f.value}>
                                {f.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="sm:col-span-2">
                    <label className="block text-xs font-display uppercase tracking-wider text-[var(--green-dark)] mb-2">
                        Bericht
                    </label>
                    <textarea
                        name="message"
                        value={state.message}
                        onChange={onChange}
                        rows={5}
                        data-testid="brochure-input-message"
                        placeholder="Vertel ons kort over je onderneming of stel je vraag…"
                        className="w-full bg-cream border-2 border-[var(--green)] rounded-2xl px-4 py-3 text-base text-[var(--green-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--green)] resize-none"
                    />
                </div>
            </div>

            {status === 'error' && (
                <div
                    role="alert"
                    data-testid="brochure-error"
                    className="mt-6 bg-red-50 border-2 border-red-300 text-red-800 rounded-2xl p-4 flex items-start gap-3"
                >
                    <AlertCircle className="flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-sm">{errMsg}</p>
                </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-xs text-[#006b44]/70">
                    We gebruiken je gegevens enkel om je de brochure te bezorgen.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full sm:w-auto">
                    {settings?.brochure_public_download_enabled && settings?.brochure_pdf_url && (
                        <a
                            href={settings.brochure_pdf_url.startsWith('http') ? settings.brochure_pdf_url : settings.brochure_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="brochure-download-link"
                            className="zz-btn zz-btn--cream w-full sm:w-auto"
                        >
                            <Download size={16} /> Download brochure (PDF)
                        </a>
                    )}
                    <button
                        type="submit"
                        disabled={status === 'submitting'}
                        data-testid="brochure-submit"
                        className="zz-btn zz-btn--primary w-full sm:w-auto disabled:opacity-50"
                    >
                        {status === 'submitting' ? 'Versturen…' : (
                            <>
                                Verzend aanvraag <Send size={16} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
};

const Field = ({ label, name, type = 'text', value, onChange, error, autoComplete, testId }) => {
    // 2026 input UX hints — inputmode for mobile keyboards, enterkeyhint for the Enter key.
    const inputMode = type === 'email' ? 'email' : type === 'tel' ? 'tel' : undefined;
    return (
        <div>
            <label className="block text-xs font-display uppercase tracking-wider text-[var(--green-dark)] mb-2">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
                inputMode={inputMode}
                enterKeyHint="next"
                data-testid={testId}
                className={`w-full min-h-[44px] bg-cream border-2 ${
                    error ? 'border-red-400' : 'border-[var(--green)]'
                } rounded-full px-4 py-3 text-base text-[var(--green-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--green)]`}
                aria-invalid={!!error}
                aria-describedby={error ? `${name}-err` : undefined}
            />
            {error && (
                <p id={`${name}-err`} className="mt-1.5 text-xs text-red-600 font-semibold">
                    {error}
                </p>
            )}
        </div>
    );
};

export default BrochureForm;
